<template>
  <div class="alerts standard-layout">
    <div class="d-flex justify-space-between flex-column flex-md-row">
      <h2 class="mb-8 standard-layout__heading">
        {{ $ucfirst($t("patient.alerts")) }}
      </h2>
      <alerts-filter
        v-model="filter"
        class="mb-8"
        key="alerts-patient-filter"
        :alertCount="alertColorCount"
      ></alerts-filter>
    </div>
    <div class="alerts__controls-top">
      <patient-search
        class="alerts__search-patients"
        v-model="patient"
        :placeholder="$ucfirst($t('patient.searchAlerts'))"
      ></patient-search>
      <div class="alerts__time-filter">
        <div class="alerts__time-from">
          <span>{{
            $ucfirst($t("general.from", { catalog: "app_mobile" }))
          }}</span>
          <alerts-date-picker
            v-model="fromDate"
            :messageMinDate="this.moment(minDate).format('YYYY-MM-DD')"
            :messageMaxDate="this.moment().format('YYYY-MM-DD')"
            :disableDateMessage="
              $t('validation.invalidDate.startOfTrialInCenter')
            "
            :datePickerAttr="fromDateRange"
          ></alerts-date-picker>
        </div>
        <div class="alerts__time-to">
          <span>{{
            $ucfirst($t("general.until", { catalog: "app_mobile" }))
          }}</span>
          <alerts-date-picker
            v-model="toDate"
            alignRight
            :messageMinDate="this.moment(minDate).format('YYYY-MM-DD')"
            :messageMaxDate="this.moment().format('YYYY-MM-DD')"
            :disableDateMessage="
              $t('validation.invalidDate.startOfTrialInCenter')
            "
            :datePickerAttr="toDateRange"
          ></alerts-date-picker>
        </div>
      </div>
    </div>

    <alerts-table class="alerts__table mb-6"
            :filters="filter"
            :patient-id="patientId"
            @update:alertCount="(v) => this.alertColorCount = v"
    ></alerts-table>
  </div>
</template>

<script>
import AlertsFilter from "../components/Alerts/AlertsFilter";
import AlertsTable from "../components/Alerts/AlertsTable";
import AlertsDatePicker from "../components/Alerts/AlertsDatePicker";
import PatientSearch from "../components/Patient/PatientSearch";
import Moment from "moment";
import { extendMoment } from "moment-range";
import gql from "graphql-tag";

/**
 * Component Alerts.vue
 *
 * @module Alerts
 * @date
 * @author
 * @copyright UniWeb bvba 2022
 *
 * @link https://gitlab.uniweb.be/cor/4.04-caregiver-dashboard
 *
 * @coropreventionSW [SW-4.1] app/src/APP_DASHBOARD/templates/views/Alerts.vue
 * @see https://docs.google.com/document/d/1iOpHH151s_ABr-DniJIc__q-GHpXJJVq/edit#heading=h.a0z8d9xd3xr7
 *
 */

const moment = extendMoment(Moment);

export default {
  name: "Alerts",
  components: {
    AlertsFilter,
    AlertsTable,
    AlertsDatePicker,
    PatientSearch,
  },
  data() {
    return {
      minDate: moment().add(-1.5, "years").format(),
      patient: {},
      alertColorCount : {
        red : 0,
        orange : 0,
        yellow : 0,
        handled: 0,
        active: 0
      }
    };
  },
  computed: {
    patientId() {
      if (this?.patient?.patientId) {
        return this.patient.patientId;
      }
      return null;
    },
    filter: {
      get() {
        return this.$store.getters["alerts/filters"];
      },
      set(filter) {
        this.$store.dispatch("alerts/setFilters", filter);
      },
    },
    fromDate: {
      get() {
        return this.$store.getters["alerts/fromDate"];
      },
      set(fromDate) {
        this.$store.dispatch("alerts/setFromDate", fromDate);
      },
    },
    toDate: {
      get() {
        return this.$store.getters["alerts/toDate"];
      },
      set(toDate) {
        this.$store.dispatch("alerts/setToDate", toDate);
      },
    },
    moment: () => moment,
    fromDateRange() {
      const range = {
        min: moment(this.minDate).format("YYYY-MM-DD"),
        max: moment().format("YYYY-MM-DD"),
      };
      if (this.toDate) {
        range.max = moment(this.toDate).format("YYYY-MM-DD");
      }
      return range;
    },
    toDateRange() {
      const range = {
        min: moment(this.minDate).format("YYYY-MM-DD"),
        max: moment().format("YYYY-MM-DD"),
      };
      if (this.fromDate) {
        range.min = moment(this.fromDate).format("YYYY-MM-DD");
      }
      return range;
    },
  },
  mounted() {
    this.setDefaultDateRange();
    this.getMinDate();
    this.$store.dispatch("user/fetchAndStoreUserData");
  },
  methods: {
    setDefaultDateRange() {
      this.$app.apolloClient
        .query({
          query: gql`
            {
              alerts(
                orders: [{ field: activateDate, direction: asc }]
                conditions: {
                  gate: and
                  inputs: [
                    {
                      field: emergencyLevel
                      operator: in
                      parameters: ["2", "3"]
                    }
                    { field: state, operator: in, parameters: ["active"] }
                  ]
                }
                limit: 1
              ) {
                edges {
                  node {
                    activateDate
                  }
                }
              }
            }
          `,
        })
        .then((result) => {
          const data = result.data?.alerts;
          if (!data) return;
          const lastDate = data.edges[0]?.node.activateDate;

          if (lastDate) {
            this.$store.dispatch(
              "alerts/setFromDate",
              moment(lastDate).format("YYYY-MM-DD")
            );
          } else {
            this.$store.dispatch(
              "alerts/setFromDate",
              moment().format("YYYY-MM-DD")
            );
          }
          this.$store.dispatch(
            "alerts/setToDate",
            moment().format("YYYY-MM-DD")
          );
        });
    },
    getMinDate() {
      this.$app.apolloMobileClient
        .query({
          query: gql`
            {
              myUser {
                minDateStartTrial
              }
            }
          `,
        })
        .then((result) => {
          const data = result.data?.myUser;
          if (!data) return;

          if (data.minDateStartTrial) {
            this.minDate = data.minDateStartTrial;
          }
        });
    },
  },
};
</script>

<style lang="scss">
.alerts {
  flex-grow: 1;
  display: flex;
  flex-direction: column;
}

.alerts__search-patients {
  flex-grow: 1;
}

.alerts__table {
  flex-grow: 1;
}

.alerts__controls-top,
.alerts__controls-bottom {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.alerts__controls-top {
  margin-bottom: 24px;
}

.alerts__limit {
  display: flex;
  align-items: center;

  .v-select {
    max-width: 82px;
    margin-right: 24px;
    margin-left: 24px;
  }
}

.alerts__time-filter,
.alerts__time-from,
.alerts__time-to {
  display: flex;
  align-items: center;
}

.alerts__time-from,
.alerts__time-to {
  margin-left: 40px;

  .date-picker__text-field {
    margin-left: 24px;
  }
}

.v-application.md-up {
  .alerts__search-patients {
    max-width: 50%;
  }
}

.alerts__controls-bottom {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.alerts__page-indicator {
  display: flex;
  align-items: center;
}
</style>
